import { useEffect } from "react";

/* ─────────────────────────────────────────────────────────────────────────────
   Cowork design system — the drawn button and the drawn underline.

   Geometry and timing are ported from the system's own source (claude.ai/design
   project "Cowork Workshop Page edits" → cowork-ds/cowork-ds.css):

     .ds-p1  1500ms @  400ms   lead stroke
     .ds-p2  1400ms @ 1000ms   ghost pass, drawn second
     .ds-ul   620ms @ 1150ms   the secondary's underline

   All three run on load — the stagger is the page-open beat.

   THE PATHS ARE BUILT IN PIXELS, not stretched from a fixed viewBox. The system
   draws one button size (238×68), so its `M7 9 L226 5 …` can live in a 232×66
   viewBox safely. Our CTAs are fluid — full width on mobile — and stretching
   that viewBox turns the 3–5px "off true" corners into huge diagonal wedges and
   makes the verticals thin while the horizontals fatten. So the corner offsets
   are held in absolute pixels at whatever size the button actually is, which is
   what keeps the hand looking like the same hand.

   Every path carries pathLength="1", so one dash covers the whole stroke no
   matter how long it is and the CSS can animate a single normalised offset.
   ───────────────────────────────────────────────────────────────────────────── */

// The comparison table closes with a stock .btn-primary rather than a
// .btn-cta, so it was the one CTA on the page still rendering as a solid
// terra block instead of the drawn frame.
/* The reminder popup renders as a direct child of <body>, outside .ds-cowork,
   so the scoped reskin never reaches it. `ds-cowork-page` on <html> gives it a
   hook — the same trick the nav flag uses — and these selectors follow it. */
const POPUP = '.ds-cowork-page [role="dialog"]';
/* The mobile reserve bar is `fixed` outside .ds-cowork too, and has no class
   of its own — it is addressed by the utility pair it is authored with. Its
   CTA was the one button on the page still rendering as a solid fill instead
   of a drawn frame. */
const STICKY = '.ds-cowork-page div[class*="z-[110]"][class*="bottom-0"]';
/* The menu panel portals outside the page too (Esther, 2026-10-08: its CTAs
   should be the house buttons on the 2026 pages). */
const MENU = ".nav-panel-root.np-vivid";
const PRIMARY = `.ds-cowork .btn-cta, .ds-cowork #final-cta .btn-lg, .ds-cowork #comparison .btn-primary, ${POPUP} .btn-cta, ${POPUP} .btn-primary, ${STICKY} button, ${MENU} .np-cta`;
const SECONDARY = `.ds-cowork .btn-outline, .ds-cowork .ds-secondary, .ds-cowork .persona-register, ${POPUP} .btn-outline, ${MENU} .np-login`;
const SVG_NS = "http://www.w3.org/2000/svg";
const RULE_H = 9; // the underline's own band, per .ds-link

/** Corner insets, read off the system's two quads at its 232×66 viewBox. */
function leadPath(w: number, h: number) {
  return `M7 9 L${w - 6} 5 L${w - 3} ${h - 8} L4 ${h - 5} Z`;
}
function ghostPath(w: number, h: number) {
  return `M4 6 L${w - 4} 9 L${w - 7} ${h - 4} L8 ${h - 9} Z`;
}
/** One hand-drawn line. Control points ride the width; the wobble stays ~3px. */
function rulePath(w: number) {
  const x = (f: number) => (w * f).toFixed(1);
  return `M1 6 C${x(0.2)} 3,${x(0.4)} 7,${x(0.62)} 4 C${x(0.76)} 2,${x(0.87)} 6,${w - 1} 4`;
}

function makePath(d: string, cls: string, strokeWidth: string) {
  const p = document.createElementNS(SVG_NS, "path");
  p.setAttribute("d", d);
  p.setAttribute("class", cls);
  p.setAttribute("pathLength", "1");
  p.setAttribute("fill", "none");
  p.setAttribute("stroke-width", strokeWidth);
  p.setAttribute("stroke-linecap", "round");
  p.setAttribute("stroke-linejoin", "round");
  return p;
}

/* 2026-09-09 — "already drawn" used to live only on `el.dataset.dsDrawn`,
   which dies with the element. The jump-nav unmounts and remounts as you
   scroll, so its CTA re-drew its outline from scratch every time it came
   back. A `data-ds-draw-key` on the button gives that memory somewhere to
   live that outlives the node. */
const drawnKeys = new Set<string>();

/** Has this button (or an earlier incarnation of it) already drawn? */
function isDrawn(el: HTMLElement): boolean {
  if (el.dataset.dsDrawn === "1") return true;
  const key = el.dataset.dsDrawKey;
  return key !== undefined && drawnKeys.has(key);
}

function makeSvg(cls: string, w: number, h: number) {
  const s = document.createElementNS(SVG_NS, "svg");
  s.setAttribute("viewBox", `0 0 ${w} ${h}`);
  s.setAttribute("aria-hidden", "true");
  s.setAttribute("class", cls);
  return s;
}

function drawPrimary(el: HTMLElement) {
  const { width, height } = el.getBoundingClientRect();
  const w = Math.round(width);
  const h = Math.round(height);
  if (w < 8 || h < 8) return; // not laid out yet

  const existing = el.querySelector<SVGSVGElement>(":scope > .ds-btn-frame");
  if (existing) {
    // Re-register on every pass, not just at creation: React runs this effect
    // twice in dev, and the second pass lands here rather than in the branch
    // below — which left every button unobserved and so never drawn.
    watch(el);
    if (existing.dataset.w === String(w) && existing.dataset.h === String(h)) return;
    // Resize in place. Opening the checkout locks body scroll, which takes the
    // scrollbar away and shifts every button a few pixels — replacing the <svg>
    // there would restart the draw, so the frame just changes shape instead.
    resize(existing, w, h);
    return;
  }

  const s = makeSvg("ds-btn-frame", w, h);
  s.append(
    makePath(leadPath(w, h), "ds-fill ds-p1", "1.6"),
    makePath(ghostPath(w, h), "ds-p2", "1.4"),
  );
  resize(s, w, h);
  // A button only draws once per page load. If React re-mounts it later the
  // frame comes back already drawn rather than animating a second time.
  if (isDrawn(el)) s.classList.add("ds-drawn");
  el.prepend(s);
  watch(el);
}

/** Re-shape a frame to a new box without replacing it (which would re-animate). */
function resize(s: SVGSVGElement, w: number, h: number) {
  s.setAttribute("viewBox", `0 0 ${w} ${h}`);
  s.dataset.w = String(w);
  s.dataset.h = String(h);
  s.querySelector(".ds-p1")?.setAttribute("d", leadPath(w, h));
  s.querySelector(".ds-p2")?.setAttribute("d", ghostPath(w, h));
}

/** The text's own box, not the button's — .btn carries 32px of side padding
 *  and the system's underline sits under the words, 3px proud each side. */
function textRect(el: HTMLElement): DOMRect | null {
  const range = document.createRange();
  let found = false;
  const rects: DOMRect[] = [];
  el.childNodes.forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === "svg") return;
    if (node.nodeType === Node.TEXT_NODE && !node.textContent?.trim()) return;
    range.selectNodeContents(node);
    const r = range.getBoundingClientRect();
    if (r.width > 0) {
      rects.push(r);
      found = true;
    }
  });
  if (!found) return null;
  const left = Math.min(...rects.map((r) => r.left));
  const right = Math.max(...rects.map((r) => r.right));
  return new DOMRect(left, rects[0].top, right - left, rects[0].height);
}

function drawSecondary(el: HTMLElement) {
  const text = textRect(el);
  if (!text) return;
  const box = el.getBoundingClientRect();
  const w = Math.round(text.width) + 6; // 3px proud at each end
  const left = Math.round(text.left - box.left) - 3;
  if (w < 14) return;

  const existing = el.querySelector<SVGSVGElement>(":scope > .ds-link-rule");
  if (existing) {
    watch(el);
    if (existing.dataset.w === String(w) && existing.dataset.left === String(left)) return;
    existing.setAttribute("viewBox", `0 0 ${w} ${RULE_H}`);
    existing.dataset.w = String(w);
    existing.dataset.left = String(left);
    existing.style.left = `${left}px`;
    existing.style.width = `${w}px`;
    existing.querySelector(".ds-ul")?.setAttribute("d", rulePath(w));
    return;
  }

  const s = makeSvg("ds-link-rule", w, RULE_H);
  s.dataset.w = String(w);
  s.dataset.left = String(left);
  s.style.left = `${left}px`;
  s.style.width = `${w}px`;
  s.append(makePath(rulePath(w), "ds-ul", "1.9"));
  if (isDrawn(el)) s.classList.add("ds-drawn");
  el.prepend(s);
  watch(el);
}

/* The nav is fixed and transparent, so it inherits whatever it happens to be
   over. The hero is now a dark teal room and the sections below it are paper,
   so the wordmark and the menu have to switch as the hero passes — a static
   override would blind one end of the page or the other. */
/* 2026-09-09 — this used to be a closure INSIDE trackNavOverHero, which is
   why processMutations could only re-run it by firing a synthetic
   `window.dispatchEvent(new Event("scroll"))` — waking every scroll listener
   on the page, Lenis included, on every DOM mutation. It is a module-level
   function now and the mutation handler calls it directly. */
let navFrame = 0;
function syncNavOverHero() {
  cancelAnimationFrame(navFrame);
  navFrame = requestAnimationFrame(() => {
    // Re-query on every tick: the nav and the hero mount at different times,
    // and capturing them once meant a reload could leave the bar dark on dark.
    const nav = document.querySelector<HTMLElement>(".ds-cowork nav");
    // `.ch-hero` is the event pages' dark hero; `.ds-hero-field` is the
    // index's, which became a dark band too when it took the members
    // dashboard's art recipe. Both need the bar to invert while over them.
    const hero = document.querySelector<HTMLElement>(
      // `.vip-band` is /vip-session's blurred-Monet hero (2026-10-13).
      ".ds-cowork .ch-hero, .ds-cowork .ds-hero-field, .ds-cowork .vip-band, .ds-cowork .nyf-hero",
    );
    if (!nav || !hero) return;
    const overHero = hero.getBoundingClientRect().bottom > nav.offsetHeight + 8;
    // The flag goes on <html>, not on the nav: React owns the nav's
    // className and wipes anything added to it on the next render.
    document.documentElement.classList.toggle("ds-nav-over-hero", overHero);
  });
}

function trackNavOverHero() {
  // The effect can attach before the hero has rendered (the page shows a
  // loading state first), and at the top of the page no scroll ever fires to
  // retry — so poll briefly until both elements exist. Scroll/resize are NOT
  // subscribed here any more; useDrawnButtons owns one listener each and fans
  // out to this, the sweep and the redraw.
  const retries = [0, 120, 400, 900, 1800].map((ms) => window.setTimeout(syncNavOverHero, ms));
  syncNavOverHero();
  return () => {
    retries.forEach(window.clearTimeout);
    cancelAnimationFrame(navFrame);
    document.documentElement.classList.remove("ds-nav-over-hero");
  };
}


/* ─────────────────────────────────────────────────────────────────────────────
   A CTA draws when the reader reaches it.

   The marks used to animate on load, on a fixed 400/1000/1150ms stagger. That
   is right for the hero and wrong for everything below it: by the time you
   scrolled to a button it had finished drawing long ago and read as a plain
   outline. The CSS now holds every mark at stroke-dashoffset 1 and runs the
   animation only under `.ds-in`, which lands here as the button arrives.

   This is a rect check on scroll rather than an IntersectionObserver. IO is
   the obvious tool and it is what this started as, but the marks are the whole
   CTA here — if the trigger never fires the button is an invisible label, so
   it cannot depend on an API that some embedded browsers do not deliver
   callbacks for (the in-app preview is one: a standalone observer on a fully
   on-screen element never fired). A rAF-throttled rect read over ~13 buttons
   is cheap, and it shares the scroll listener pattern the nav tracker already
   uses.

   `dsDrawn` is stamped on ARRIVAL, not at creation, so a button React
   re-mounts before you ever reach it still draws when you get there; one that
   has already drawn comes back finished instead of animating twice.
   ───────────────────────────────────────────────────────────────────────────── */
const MARKS = ":scope > .ds-btn-frame, :scope > .ds-link-rule";
/** Buttons with a mark that have not arrived yet. */
let pending = new Set<HTMLElement>();
let sweepFrame = 0;

function reveal(el: HTMLElement) {
  el.dataset.dsDrawn = "1";
  // Same moment `dsDrawn` is stamped (arrival, not animation end): a button
  // that unmounts mid-draw should come back finished, not restart.
  const key = el.dataset.dsDrawKey;
  if (key) drawnKeys.add(key);
  el.querySelectorAll<SVGSVGElement>(MARKS).forEach((s) => {
    s.classList.add("ds-in");
    // Retire `ds-in` for `ds-drawn` once the sweep has actually run.
    //
    // Without this a mark keeps a LIVE `ds-draw` animation on it forever, and
    // the hover rule cancels that animation with `animation: none`. Cancelling
    // is fine; the problem is releasing the hover, which restores the name and
    // therefore RESTARTS the animation from the top — including its 400ms
    // delay, during which the base `stroke-dashoffset: 1` applies. So every
    // mouse-out blinked the underline out and swept it back in. `ds-drawn`
    // holds it finished with `animation: none`, so hovering has nothing left
    // to cancel and the line just sits there.
    s.addEventListener("animationend", () => {
      s.classList.remove("ds-in");
      s.classList.add("ds-drawn");
    }, { once: true });
  });
}

/** Reveal everything that has come far enough into view. */
function sweep() {
  if (!pending.size) return;
  // Hold the trigger 8% off the bottom edge so the draw starts once the button
  // has actually arrived, not on the frame it clips the fold.
  const line = window.innerHeight * 0.92;
  // Snapshot first: reveal() mutates the set as it goes. (Array.from rather
  // than a spread — the tsconfig target predates downlevel Set iteration.)
  for (const el of Array.from(pending)) {
    const r = el.getBoundingClientRect();
    if (r.height === 0 || r.width === 0) continue; // hidden (the mobile copies)
    const shown = Math.min(r.bottom, line) - Math.max(r.top, 0);
    if (shown >= Math.min(r.height * 0.25, 24)) {
      reveal(el);
      pending.delete(el);
    }
  }
}

/* Scroll and resize go through a frame, so a fast scroll costs one rect read
   per paint rather than one per event. */
function scheduleSweep() {
  cancelAnimationFrame(sweepFrame);
  // No frames are coming while the tab is hidden, so a queued one would just
  // sit there. Read directly instead — there is no paint to batch against.
  if (document.hidden) {
    sweep();
    return;
  }
  sweepFrame = requestAnimationFrame(sweep);
}

/* Everything else sweeps directly. A hidden tab gets no animation frames at
   all — and the marks ARE the buttons here, so a trigger that can be starved
   of frames would leave a page of invisible labels. Timers still run. */
function sweepNow() {
  cancelAnimationFrame(sweepFrame);
  sweep();
}

function watch(el: HTMLElement) {
  // Already drawn: the `.ds-drawn` class added at creation holds it finished.
  if (isDrawn(el) || pending.has(el)) return;
  pending.add(el);
  sweepNow();
}


/** The menu panel draws its own buttons: on Kids and The AI-First Leader no
 *  useDrawnButtons is running to do it for it. */
export function drawMenuButtons(root: ParentNode = document) {
  root.querySelectorAll<HTMLElement>(".nav-panel-root.np-vivid .np-cta").forEach(drawPrimary);
  root.querySelectorAll<HTMLElement>(".nav-panel-root.np-vivid .np-login").forEach(drawSecondary);
}

export function useDrawnButtons(enabled: boolean, variant?: string) {
  useEffect(() => {
    if (!enabled) return;

    const untrackNav = trackNavOverHero();
    // marks the whole document while a ds-cowork page is mounted, so surfaces
    // that portal out of the page (the popup) can still be reached
    document.documentElement.classList.add("ds-cowork-page");
    // ...and the palette variant with it. The popup portals to <body>, so the
    // page div's `.ds-lav` is not an ancestor of it and its tokens cannot
    // reach; `ds-lav-page` on <html> is the same trick one level up.
    const variantClass = variant ? `${variant}-page` : null;
    if (variantClass) document.documentElement.classList.add(variantClass);

    /* Lenis runs the page's smooth scrolling and swallows wheel events, so a
       nested scroller — the popup, which can be taller than the viewport —
       never receives them and cannot be scrolled to its submit button. Lenis
       opts out on `data-lenis-prevent`, which has to be set on the element
       itself; there is no CSS equivalent. */
    const markScrollers = () => {
      document.querySelectorAll<HTMLElement>('[role="dialog"]').forEach((el) => {
        if (!el.hasAttribute("data-lenis-prevent")) el.setAttribute("data-lenis-prevent", "");
      });
    };
    markScrollers();

    pending = new Set<HTMLElement>();

    const draw = () => {
      document.querySelectorAll<HTMLElement>(PRIMARY).forEach(drawPrimary);
      document.querySelectorAll<HTMLElement>(SECONDARY).forEach(drawSecondary);
    };

    draw();

    let drawFrame = 0;
    const scheduleDraw = () => {
      cancelAnimationFrame(drawFrame);
      drawFrame = requestAnimationFrame(draw);
    };

    /* 2026-09-09 — scroll was subscribed TWICE (nav sync + sweep) and resize
       THREE times (nav sync + sweep + redraw) from this one effect, so every
       wheel notch woke two handlers and every resize woke three. One listener
       each now, fanning out to the same three jobs. */
    const onScroll = () => {
      syncNavOverHero();
      scheduleSweep();
    };
    const onResize = () => {
      syncNavOverHero();
      scheduleSweep();
      scheduleDraw();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);

    // Fonts and images settle after the first paint and move buttons around,
    // and at the top of the page no scroll ever fires to re-check — so sweep a
    // few times on the way in.
    const sweeps = [0, 120, 400, 900, 1800].map((ms) => window.setTimeout(sweepNow, ms));
    // A tab restored from the background has a page of undrawn marks and no
    // scroll to prompt a re-check.
    document.addEventListener("visibilitychange", sweepNow);

    // React can re-render a button and drop the mark; a button can also change
    // width (viewport, sticky bar appearing). Both need a redraw.
    // Coalesced into one frame. This fires on ANY DOM change under <body>, and
    // draw() reads getBoundingClientRect for every button — running that
    // synchronously per mutation batch forces a layout each time, and draw()
    // then mutates the DOM itself, which can feed the observer again.
    let moFrame = 0;
    const processMutations = () => {
      moFrame = 0;
      markScrollers();
      draw();
      scheduleSweep();
      // 2026-09-09: was `window.dispatchEvent(new Event("scroll"))`, which woke
      // every scroll listener on the page (Lenis included) on every DOM
      // mutation just to re-check one class on <html>. Call it directly.
      syncNavOverHero();
    };
    const mo = new MutationObserver(() => {
      if (moFrame) return;
      // A hidden tab gets no animation frames, so a queued one would never
      // run and anything that mounted while hidden — the popup, for one —
      // would never get its drawn frame.
      if (document.hidden) {
        processMutations();
        return;
      }
      moFrame = requestAnimationFrame(processMutations);
    });
    /* Still the whole <body>, deliberately. The surfaces that most need a
       redraw — the reminder popup and the mobile reserve bar — are React
       portals mounted as fresh children of <body>, so an observer scoped to
       <main> could not see the mutation that creates them, and a mark that
       never draws is an invisible button. */
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      mo.disconnect();
      cancelAnimationFrame(moFrame);
      sweeps.forEach(window.clearTimeout);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", sweepNow);
      cancelAnimationFrame(sweepFrame);
      cancelAnimationFrame(drawFrame);
      untrackNav();
      document.documentElement.classList.remove("ds-cowork-page");
      if (variantClass) document.documentElement.classList.remove(variantClass);
    };
  }, [enabled, variant]);
}
